// Schedule the next request only after the previous one finishes. Each consumer
// owns its timer; changing a panel never stops another panel's refresh.
export function createDashboardPoller(task, interval, {
  document: doc = typeof document === "undefined" ? null : document,
  setTimeout: schedule = setTimeout,
  clearTimeout: cancel = clearTimeout,
  onError = error => console.warn("Dashboard refresh failed", error),
} = {}) {
  let stopped = false;
  let running = false;
  let timer = null;
  const visible = () => !doc || !doc.hidden;
  const enqueue = delay => {
    if (!stopped && visible() && !running && timer === null) {
      timer = schedule(run, delay);
    }
  };
  const run = async () => {
    timer = null;
    if (stopped || !visible() || running) return;
    running = true;
    try { await task(); } catch (error) { onError(error); }
    finally { running = false; enqueue(interval); }
  };
  const visibilityChanged = () => {
    if (timer !== null) { cancel(timer); timer = null; }
    if (visible()) enqueue(0);
  };
  if (doc) doc.addEventListener("visibilitychange", visibilityChanged);
  enqueue(interval);
  return () => {
    stopped = true;
    if (timer !== null) cancel(timer);
    timer = null;
    if (doc) doc.removeEventListener("visibilitychange", visibilityChanged);
  };
}

// Also support timers created by the previous implementation during hot reload.
export function stopDashboardTimer(timer) {
  if (typeof timer === "function") timer();
  else if (timer !== null && timer !== undefined) clearInterval(timer);
}

export default {
  beforeDestroy() { this.stopDashboardPolling(); },
  methods: {
    startDashboardPolling(task, interval) {
      this.stopDashboardPolling();
      this._stopDashboardPoller = createDashboardPoller(task, interval);
    },
    stopDashboardPolling() {
      if (this._stopDashboardPoller) this._stopDashboardPoller();
      this._stopDashboardPoller = null;
    },
  },
};
